/**
 * #C6: upload view for a carve-out placeholder report (status 'awaiting_upload').
 * When management requests a SOC 1 for a subservice org, a placeholder report is
 * created; opening it presents the SAME "Upload SOC 1" popup used in the main
 * Reports view (UploadModal) — all fields (PDF, report name, cover FY), pre-filled
 * from the placeholder — instead of a stripped-down custom form. Uploading flips
 * it to 'uploading' (ingest runs) and clears the carve-out's "Get SOC 1" item.
 */
import { useState } from "react";
import { Box, Button, Container, Header, SpaceBetween } from "@cloudscape-design/components";
import type { Report } from "@/core/api/types";
import { UploadModal } from "./UploadModal";

export function PlaceholderUpload({
  report,
  onBack,
  onUploaded,
}: {
  report: Report;
  onBack: () => void;
  onUploaded: () => void;
}) {
  // Open the standard popup straight away — the user came here to upload.
  const [modalOpen, setModalOpen] = useState(true);

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <Button onClick={onBack} iconName="arrow-left">
            Back to reports
          </Button>
        }
      >
        {report.service_org_name || "Subservice organization"}
      </Header>
      <Container header={<Header variant="h2">Upload the SOC 1</Header>}>
        <SpaceBetween size="m">
          <Box color="text-body-secondary">
            This subservice organization needs its own SOC 1. Upload its PDF — it's reviewed like any other
            report, and the "Get SOC 1" action item clears as soon as it's uploaded.
          </Box>
          <Button variant="primary" iconName="upload" onClick={() => setModalOpen(true)}>
            Upload SOC 1
          </Button>
        </SpaceBetween>
      </Container>
      <UploadModal
        visible={modalOpen}
        existingReport={report}
        onDismiss={() => setModalOpen(false)}
        onUploaded={() => {
          setModalOpen(false);
          onUploaded(); // status → 'uploading'; the parent's ingest poll takes over.
        }}
      />
    </SpaceBetween>
  );
}
